// ── Bienvenida tras el pago (2026-08-19) ─────────────────────────────────────
// Antes, volver de Stripe aterrizaba en Ajustes → Suscripción: la pantalla de
// configuración, sin ninguna confirmación. El cliente acababa de pagar y no
// veía nada que le dijera que había funcionado.
//
// Esta es la primera vez que ve su cuenta viva, así que confirma las tres cosas
// que importan en ese momento —el plan está activo, la inducción terminó, y por
// dónde se empieza— y no compite con nada más.
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettingsTabLabel } from '../components/ui/SettingsLink.js';
import { useNavigate } from 'react-router-dom';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { Card } from '../components/ui/Card.js';
import { Button } from '../components/ui/Button.js';
import { Icon } from '../components/Icon.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { useSubscription } from '../hooks/useSettings.js';

function fmtMoney(cents: number, currency: string | null): string {
  return new Intl.NumberFormat('es', { style: 'currency', currency: (currency ?? 'usd').toUpperCase() })
    .format(cents / 100);
}

export function WelcomePage() {
  const { t } = useTranslation();
  const tabSuscripcion = useSettingsTabLabel('account');
  const navigate = useNavigate();
  const { setOverride } = useTopBarCtx();
  // refetchInterval: el webhook de Stripe puede tardar unos segundos en llegar,
  // así que la cuenta todavía puede figurar impaga al aterrizar acá. Se
  // reconsulta hasta que el estado se acomoda, en vez de mostrar un "no pagaste"
  // que es falso y que es justo el momento donde más asusta.
  const { data, isLoading } = useSubscription();

  useEffect(() => {
    setOverride({
      title: t('welcome.title'),
      subtitle: t('welcome.subtitle'),
      heroIcon: 'bolt',
      heroIconColor: 'var(--evo-cyan)',
    });
    return () => setOverride({});
  }, [t, setOverride]);

  const activa = data?.status === 'active';
  const proximo = data?.nextChargeAt
    ? new Date(data.nextChargeAt).toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <div
      className="animate-fade-blur-in"
      style={{ height: '100%', overflowY: 'auto', padding: 'var(--evo-space-6) var(--evo-content-pad-x)' }}
    >
      <div style={{ maxWidth: '560px' }}>
        {isLoading && <LoadingState />}

        {!isLoading && (
          <Card padding="lg">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)' }}>
                <span style={{ color: activa ? 'var(--evo-success)' : 'var(--evo-warning)', display: 'inline-flex' }}>
                  <Icon name={activa ? 'ok' : 'alert'} size={20} />
                </span>
                <span style={{ fontFamily: 'var(--font-display)', color: 'var(--evo-fg-1)' }}>
                  {activa ? t('welcome.activeTitle') : t('welcome.pendingTitle')}
                </span>
              </div>

              <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', margin: 0, lineHeight: 1.6 }}>
                {activa ? t('welcome.activeBody') : t('welcome.pendingBody', { tab: tabSuscripcion })}
              </p>

              {activa && (data?.planName || data?.priceAmount != null) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-1)' }}>
                  {data.planName && (
                    <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-1)' }}>{data.planName}</span>
                  )}
                  {data.priceAmount != null && (
                    <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                      {fmtMoney(data.priceAmount, data.priceCurrency)}
                      {data.priceInterval ? ` / ${t(`settings.subscription.interval.${data.priceInterval}`, data.priceInterval)}` : ''}
                    </span>
                  )}
                  {proximo && (
                    <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
                      {t('welcome.nextCharge', { date: proximo })}
                    </span>
                  )}
                </div>
              )}

              <div>
                <Button variant="solid" tone="cyan" size="sm" onClick={() => navigate('/', { replace: true })}>
                  {t('welcome.startBtn')}
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
